import { FaBus, FaChargingStation, FaMap, FaTruck } from "react-icons/fa";
import {
  LuChartBar,
  LuClipboardList,
  LuDatabase,
  LuGitBranch,
  LuGlobe,
  LuLayoutDashboard,
  LuLightbulb,
  LuMap,
  LuMessageSquare,
  LuReply,
  LuTable,
  LuUsers,
  LuCar,
} from "react-icons/lu";
import { HiOutlineTable } from "react-icons/hi";
import { enableInternalFleetTools } from "./env";
import { CA_HD_2027_REPORT_PATH, CA_TRANSIT_REPORT_PATH } from "./publishedReports";

const GRIDPLAN_ONLY = { requiresProductAccess: "gridplan" };

const sharedHomeItem = {
  icon: LuReply,
  text: "Solution Hub",
  link: "/home",
};

export const fleetNavConfig = {
  productName: "eFleetPlan",
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Main",
      items: [
        { icon: LuLayoutDashboard, text: "Overview", link: "/fleet/dashboard" },
        { icon: LuClipboardList, text: "Inputs", link: "/fleet/inputs" },
        {
          icon: LuChartBar,
          text: "Latest Result",
          link: "/fleet/analytics/latest",
          activePaths: ["/fleet/analytics/latest", "/fleet/analytics/cases"],
        },
        { icon: LuClipboardList, text: "Case Library", link: "/fleet/analytics", exact: true },
      ],
    },
    ...(enableInternalFleetTools
      ? [{
        label: "Dev access",
        items: [
          {
            icon: LuMessageSquare,
            text: "LLM Model Selection",
            link: "/fleet/internal/llm-models",
            requiresAmptransEmail: true,
          },
          {
            icon: LuLightbulb,
            text: "Fleet Data Center",
            link: "/fleet/internal/preview",
            requiresAmptransEmail: true,
          },
        ],
      }]
      : []),
  ],
  actions: [
    {
      key: "feedback",
      icon: LuMessageSquare,
      text: "Feedback",
      type: "feedback",
    },
  ],
  resources: [
    { label: "User Manual", to: "/fleet/manual", external: true },
    { label: "Technical Details", to: "/fleet/technical-details", external: true },
    { label: "Changelog", to: "/fleet/changelog" },
    { type: "divider" },
    { label: "Contact Support", href: "mailto:info@amptrans.net", external: true },
  ],
};

export const chargerNavConfig = {
  productName: "ChargerPlan",
  feedbackStepOptions: [
    { id: "overview", label: "Overview" },
    { id: "browse-parcels", label: "Browse Parcels" },
    { id: "compare-parcels", label: "Compare Parcels" },
    { id: "general", label: "General" },
  ],
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Main",
      items: [
        { icon: LuLayoutDashboard, text: "Overview", link: "/charger/dashboard" },
        { icon: LuMap, text: "Browse Parcels", link: "/charger/browse" },
        { icon: LuTable, text: "Compare Parcels", link: "/charger/compare" },
      ],
    },
  ],
  actions: [
    {
      key: "feedback",
      icon: LuMessageSquare,
      text: "Feedback",
      type: "feedback",
    },
  ],
  resources: [
    { label: "User Manual", to: "/charger/manual", external: true },
    { label: "Technical Details", to: "/charger/technical-details", external: true },
    { label: "Changelog", to: "/charger/changelog" },
    { type: "divider" },
    { label: "Contact Support", href: "mailto:info@amptrans.com", external: true },
  ],
};

export const utilityNavConfig = {
  productName: "GridPlan",
  feedbackStepOptions: [
    { id: "overview", label: "Overview" },
    { id: "workflow", label: "Workflow" },
    { id: "latest-result", label: "Latest Result" },
    { id: "case-library", label: "Case Library" },
    { id: "general", label: "General" },
  ],
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Main",
      items: [
        { icon: LuLayoutDashboard, text: "Overview", link: "/utility/dashboard", ...GRIDPLAN_ONLY },
        { icon: LuGitBranch, text: "Workflow", link: "/utility/workflow", ...GRIDPLAN_ONLY },
        {
          icon: LuChartBar,
          text: "Latest Result",
          link: "/utility/scenarios/latest",
          activePaths: ["/utility/scenarios/latest", "/utility/scenarios/cases"],
          ...GRIDPLAN_ONLY,
        },
        { icon: LuClipboardList, text: "Case Library", link: "/utility/scenarios", exact: true, ...GRIDPLAN_ONLY },
      ],
    },
    {
      label: "Case Studies",
      items: [
        {
          icon: FaMap,
          text: "CA HD Forecast 2027",
          link: CA_HD_2027_REPORT_PATH,
          activePaths: [CA_HD_2027_REPORT_PATH],
        },
        {
          icon: FaBus,
          text: "CA Load Forecast",
          link: CA_TRANSIT_REPORT_PATH,
          activePaths: [CA_TRANSIT_REPORT_PATH],
          ...GRIDPLAN_ONLY,
        },
      ],
    },
    {
      label: "Legacy",
      items: [
        { icon: LuCar, text: "Vehicle Simulation", link: "/utility/vehicle-simulation", ...GRIDPLAN_ONLY },
      ],
    },
    {
      label: "Upcoming",
      items: [
        { icon: LuLightbulb, text: "Proactive Planning", link: "/utility/planning", ...GRIDPLAN_ONLY },
      ],
    },
  ],
  actions: [
    {
      key: "feedback",
      icon: LuMessageSquare,
      text: "Feedback",
      type: "feedback",
    },
  ],
  resources: [
    { label: "User Manual", href: "", badge: "coming-soon", ...GRIDPLAN_ONLY },
    { label: "Technical Details", href: "", badge: "coming-soon", ...GRIDPLAN_ONLY },
    { label: "Changelog", to: "/utility/changelog", ...GRIDPLAN_ONLY },
    { label: "Support", href: "", badge: "coming-soon", ...GRIDPLAN_ONLY },
  ],
};

export const dataHubNavConfig = {
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Explore",
      items: [
        { icon: LuLayoutDashboard, text: "Data Hub Home", link: "/data" },
        { icon: LuChartBar, text: "Utility Rates", link: "/data/rates" },
        { icon: FaMap, text: "Territories", link: "/data/territories" },
        { icon: FaChargingStation, text: "Charging Stations", link: "/data/charging-stations" },
        { icon: FaTruck, text: "Vehicles", link: "/data/vehicles" },
      ],
    },
  ],
  resources: [
    { label: "Changelog", to: "/data/changelog" },
    { type: "divider" },
    { label: "Contact Support", href: "mailto:info@amptrans.net", external: true },
  ],
};

export const internalOpsNavConfig = {
  productName: "Internal Ops",
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Overview",
      items: [
        { icon: LuUsers, text: "Users", link: "/internal/users" },
        { icon: LuChartBar, text: "API usage", link: "/internal/api-usage" },
        { icon: LuDatabase, text: "Database", link: "/internal/database" },
        { icon: LuGlobe, text: "Site traffic", link: "/internal/traffic" },
        { icon: LuMap, text: "Sign-in map", link: "/internal/sign-ins" },
      ],
    },
  ],
  resources: [
    { label: "Contact Support", href: "mailto:info@amptrans.net", external: true },
  ],
};

export const sageNavConfig = {
  productName: "Sage",
  feedbackStepOptions: [{ id: "map", label: "Map" }],
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Main",
      items: [{ icon: LuMap, text: "Map", link: "/sage/map" }],
    },
  ],
  actions: [],
  resources: [
    { type: "divider" },
    { label: "Contact Support", href: "mailto:info@amptrans.net", external: true },
  ],
};

export const regulatorNavConfig = {
  productName: "PolicyPlan",
  feedbackStepOptions: [
    { id: "map", label: "Map" },
    { id: "table", label: "Table" },
    { id: "general", label: "General" },
  ],
  homeItem: sharedHomeItem,
  sections: [
    {
      label: "Main",
      items: [
        { icon: LuMap, text: "Map", link: "/regulator/map" },
        { icon: HiOutlineTable, text: "Table", link: "/regulator/table" },
      ],
    },
  ],
  actions: [
    {
      key: "feedback",
      icon: LuMessageSquare,
      text: "Feedback",
      type: "feedback",
    },
  ],
  resources: [
    { label: "User Manual", to: "/regulator/manual", external: true },
    { label: "Technical Details", to: "/regulator/technical-details", external: true },
    { label: "Changelog", to: "/regulator/changelog" },
    { type: "divider" },
    { label: "Contact Support", href: "mailto:info@amptrans.net", external: true },
  ],
};
